import { GoogleMap, Polygon, Marker } from '@react-google-maps/api';
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  Box,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Delete
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';

import usePermissions from '../../../hooks/usePermissions';
import FullscreenMapButton from '../../FullscreenMapButton/FullscreenMapButton';
import { defaultCenter, zonesColors } from '../../../pages/ZonesPage/constants';
import { darkMapStyles } from '../../../resources/constants';
import { MapLatLng } from '../../../pages/ZonesPage/interface';
import { useDispatch, useSelector } from 'react-redux';
import { createZoneMarkerAsync, deleteZoneMarkerAsync, getZoneMarkersAsync, getZonesAsync, selectZones, updateZoneMarkerAsync } from '../../../redux/slices/zonesSlice';
import ZoneMapMarker from './ZoneMapMarker';
import { ZoneMarker, ZoneMarkerType } from '../../../resources/types/zonesTypes';

interface Props { 
  mapCenter?: MapLatLng
  currentCityId?: string;
}

const zoneMarkerTypes = [
  {
    name: 'Заборонено паркування',
    value: 'no-parking'
  },
  {
    name: 'Дозволено паркування',
    value: 'parking'
  },
  {
    name: 'Кешбек',
    value: 'cashback'
  },
  {
    name: 'Штраф',
    value: 'payzone'
  },
  {
    name: 'Повільно',
    value: 'slow'
  },
  {
    name: 'Стоп',
    value: 'stop'
  },
  {
    name: 'Вигрузка самокатів',
    value: 'unloading'
  }
];

const defaultZoneMarkerType = 'no-parking';

const MapMarkersSection = ({ mapCenter = defaultCenter, currentCityId }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const checkPermission = usePermissions();
  const { zones, zoneMarkers } = useSelector(selectZones);

  const [type, setType] = useState<ZoneMarkerType>(defaultZoneMarkerType);
  const [selectedMarker, setSelectedMarker] = useState<ZoneMarker | null>(null);
  const [selectedMarkerCoords, setSelectedMarkerCoords] = useState<MapLatLng | null>(null);
  const [createMarker, setCreateMarker] = useState<MapLatLng | null>(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  const isEditMarker = useMemo(() => Boolean(selectedMarker), [selectedMarker]);
  useEffect(() => {
    if (isEditMarker && selectedMarker) {
      setType(selectedMarker?.type ?? defaultZoneMarkerType);
      setSelectedMarkerCoords({ lat: selectedMarker?.coordinates[0], lng: selectedMarker?.coordinates[1]});
    }
  }, [isEditMarker, selectedMarker])

  useEffect(() => {
    dispatch(getZoneMarkersAsync());
    dispatch(getZonesAsync(true));
  }, [dispatch]);

  const handleCreateMarker = () => {
    if (createMarker) {
      dispatch(createZoneMarkerAsync({ cityId: currentCityId ,type, coordinates: [createMarker?.lat, createMarker?.lng] }))
      setCreateMarker(null);
      setSelectedMarker(null);
    }
  }

  const handleUpdateMarker = () => {
    if (selectedMarker?._id && selectedMarkerCoords) {
      dispatch(updateZoneMarkerAsync(selectedMarker?._id, { cityId: currentCityId, type, coordinates: [selectedMarkerCoords?.lat, selectedMarkerCoords?.lng] }))
      setCreateMarker(null);
      setSelectedMarker(null);
    }
  }

  const handleDeleteMarker = () => {
    if (selectedMarker?._id) {
      dispatch(deleteZoneMarkerAsync(selectedMarker?._id));
      setCreateMarker(null);
      setSelectedMarker(null);
    }
  }

  return (
    <>
      {checkPermission('map') && (
        <>
          <Alert severity="info" sx={{ marginBottom: 2 }}>
            <AlertTitle>Інструкція по роботі з маркерами</AlertTitle>
            <strong>Створити маркер:</strong> Двічі клацніть на зоні (кольоровій області) на карті<br />
            <strong>Редагувати маркер:</strong> Двічі клацніть на існуючому маркері, потім клацніть на зоні для нової позиції<br />
            <strong>Видалити маркер:</strong> Двічі клацніть на маркері і натисніть кнопку "Видалити"
          </Alert>
          {
            createMarker || isEditMarker ? (
              <Grid item marginBottom={2}>
                <TextField
                  select
                  label='Тип'
                  value={type}
                  onChange={e => setType(e.target.value as ZoneMarkerType)}
                >
                  {zoneMarkerTypes.map(zoneMarkerType => (
                    <MenuItem key={zoneMarkerType.value} value={zoneMarkerType.value}>
                      {zoneMarkerType.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  sx={{ marginLeft: 2 }}
                  color='primary'
                  variant='contained'
                  onClick={() => isEditMarker ? handleUpdateMarker() : handleCreateMarker()}
                >
                  {isEditMarker ? "Оновити" : "Створити"}
                </Button>
                {
                  isEditMarker ? (
                    <>
                      <Button
                        sx={{ marginLeft: 2 }}
                        color='error'
                        variant='contained'
                        onClick={() => handleDeleteMarker()}
                      >
                        <Delete />
                      </Button>
                    </>
                  ) : null
                }
                <Button
                  sx={{ marginLeft: 2 }}
                  color='info'
                  variant='contained'
                  onClick={() => {
                    setCreateMarker(null);
                    setSelectedMarker(null);
                  }}
                >
                  Скасувати
                </Button>
              </Grid>
            ) : null
          }
          <Box ref={mapContainerRef} sx={{ position: 'relative' }}>
            <FullscreenMapButton 
              containerRef={mapContainerRef}
              onFullscreenChange={setIsMapFullscreen}
            />
            <GoogleMap
              center={mapCenter}
              mapContainerStyle={{
                width: '100%',
                height: isMapFullscreen ? '100vh' : 'calc(100vh - 280px)'
              }}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                fullscreenControl: false,
                styles: theme.palette.mode === 'dark' ? darkMapStyles : undefined
              }}
              zoom={11}
              onZoomChanged={() => {

              }}
            >
            {
              zoneMarkers && zoneMarkers?.map((marker) => (
                <ZoneMapMarker 
                  key={marker._id} 
                  marker={marker} 
                  isEdit={selectedMarker?._id === marker._id}
                  updatedCoords={selectedMarkerCoords}
                  updateType={type}
                  onSelect={() => {
                    setSelectedMarker(marker);
                    setCreateMarker(null);
                  }} 
                />
              ))
            }
            {zones && zones?.map(zone => (
              <Polygon
                key={zone._id}
                options={zonesColors[zone.type] || zonesColors.green}
                paths={zone.coordinates.map(coordinate => ({
                  lat: coordinate[0],
                  lng: coordinate[1]
                }))}
                onDblClick={(e) => {
                  if (createMarker === null && e.latLng) {
                    e.stop();
                    console.log('-=-=- Polygon onDblClick', {lat: e.latLng.lat(), lng: e.latLng.lng()})
                    setCreateMarker({ lat: e.latLng.lat(), lng: e.latLng.lng()})
                  }
                }}
                onClick={(e) => {
                  if ((createMarker !== null || selectedMarker !== null) && e.latLng) {
                    e.stop();
                    console.log('-=-=- Polygon onClick', {lat: e.latLng.lat(), lng: e.latLng.lng()})
                    if (selectedMarker !== null) {
                      setSelectedMarkerCoords({ lat: e.latLng.lat(), lng: e.latLng.lng()})
                    } else {
                      setCreateMarker({ lat: e.latLng.lat(), lng: e.latLng.lng()})
                    }
                  }
                }}
              />
            ))}
              {createMarker !== null ? (
                <Marker 
                  position={{ lat: createMarker.lat, lng: createMarker.lng }} 
                  animation={typeof window !== 'undefined' && window.google?.maps ? window.google.maps.Animation.DROP : undefined}
                />
              ) : null}
            </GoogleMap>
          </Box>
        </>
      )}
    </>
  )
}

export default MapMarkersSection;
